<script lang="ts" setup>
import { computed, onMounted } from 'vue'

import { useSourcepointStore } from '@toni/rendering/src/stores/sourcepoint'
import { useConfigStore } from '@toni/rendering/src/stores/config'

import { useClient } from '../../utils/client-only'

declare global {
  interface Window {
    __tcfapi: (
      command: string,
      version: number,
      callback: (data: unknown, success: boolean) => void,
      vendorIds: string[],
      purposeIds: string[],
      legitimateInterestPurposeIds: string[]
    ) => void
    _sp_?: {
      gdpr?: {
        loadPrivacyManagerModal: (id: string) => void
      }
    }
  }
}

const props = defineProps<{
  vendor: string
  vendorId?: string
  hasPurSub: boolean
}>()

const { isClient } = useClient()
const sourcepointStore = useSourcepointStore()
const configStore = useConfigStore()

const embedPurposeId = configStore.rsConfig.consent.embedPurposeId

const isAllowedToRender = computed(() => {
  if (props.hasPurSub) {
    return false
  }

  if (props.vendor === 'video') {
    return sourcepointStore.consentedVendors.includes(configStore.rsConfig.consent.vendor.jwplayer)
  }

  if (props.vendor === 'iframe') {
    return props.vendorId &&
           props.vendorId !== 'none' &&
           sourcepointStore.consentedVendors.includes(props.vendorId)
  }

  return sourcepointStore.consentedVendors.includes(configStore.rsConfig.consent.vendor[props.vendor])
})

const updateConsentState = (consentedVendors: string[]) => {
  sourcepointStore.$patch({ consentedVendors })
  localStorage.setItem('sp_vendor_consent', JSON.stringify(consentedVendors))
}

const onConsentClick = async () => {
  if (props.hasPurSub) return

  // For iframe and video, use privacy manager modal
  if (props.vendor === 'iframe' || props.vendor === 'video') {
    if (window._sp_?.gdpr?.loadPrivacyManagerModal && sourcepointStore.consentConfig?.privacyManagerId) {
      try {
        window._sp_.gdpr.loadPrivacyManagerModal(sourcepointStore.consentConfig.privacyManagerId)
      } catch (error) {
        console.error('Failed to load privacy manager modal:', error)
      }
    }
    return
  }

  // For embeds, use custom consent
  const allVendorIds = Object.values(configStore.rsConfig.consent.vendor) as string[]
  await new Promise(resolve => {
    window.__tcfapi(
      'postCustomConsent',
      2,
      (_, success) => {
        if (success) updateConsentState(allVendorIds)
        resolve(success)
      },
      allVendorIds,
      [embedPurposeId],
      []
    )
  }).catch(error => console.error('Consent error:', error))
}

onMounted(() => {
  if (!isClient) return

  // Initial consent from localStorage
  const storedConsent = localStorage.getItem('sp_vendor_consent')
  if (storedConsent) {
    updateConsentState(JSON.parse(storedConsent))
  }

  // Listen for privacy modal changes
  window.__tcfapi(
    'addEventListener',
    2,
    (tcData: unknown, success: boolean) => {
      if (success && typeof tcData === 'object' && tcData && 'eventStatus' in tcData) {
        window.__tcfapi(
          'getCustomVendorConsents',
          2,
          (data: unknown, success: boolean) => {
            if (success && typeof data === 'object' && data && 'grants' in data) {
              const grants = (data as { grants: Record<string, { vendorGrant: boolean }> }).grants
              const consentedVendors = Object.entries(grants)
                .filter(([, grant]) => grant.vendorGrant)
                .map(([vendorId]) => vendorId)
              updateConsentState(consentedVendors)
            }
          },
          [],
          [embedPurposeId],
          []
        )
      }
    },
    [],
    [embedPurposeId],
    []
  )
})
</script>

<template>
  <div>
    <template v-if="!isClient">
      <slot name="disabled" />
    </template>
    <template v-else>
      <slot v-if="isAllowedToRender"
            name="enabled" />
      <slot v-else
            name="disabled"
            :on-consent-click="onConsentClick" />
    </template>
  </div>
</template>
